import {
    AssessmentTypeModel,
    AssessmentTypeWriteModel,
    CommentTypeModel,
    CommentTypeWriteModel,
    IAssessmentTypeWriteModel,
    ICommentTypeWriteModel,
    IMarkTypeWriteModel,
    MarkTypeModel,
    MarkTypeWriteModel,
} from "../api/APIClient";
import { assessmentTypesClient, commentsClient } from "../api/clients";

/**
 * The organization-scoped vocabularies: mark types, assessment types, assessment-group types and
 * comment types.
 *
 * These are REST-ish endpoints (bare payloads, real status codes, ProblemDetails on failure), so no
 * envelope unwrapping is needed - unlike the Structure and Management stores.
 *
 * Store modules are thin: types plus one function per route, no caching and no state, following
 * ScadsysIdentity's dataMaintenance/DataMaintenanceStore.ts - the cleanest of its store styles.
 *
 * Write functions take the generated `I*` interface and construct the DTO class here. NSwag emits
 * request bodies as classes with `init`/`toJSON`, so a plain object literal does not satisfy the
 * parameter type; doing the construction once per route keeps every call site free of that detail.
 */

export type { AssessmentTypeModel, MarkTypeModel, CommentTypeModel };

// ---------------------------------------------------------------- mark types

/**
 * The axis on which several different marks coexist on the same entity - a raw assessment mark, a
 * division mark, a term mark, a year mark. MarkResult is unique on
 * (organization, user, entity type, entity id, mark type), so one entity holds many marks of
 * different types; that is exactly what makes chained procedures possible.
 *
 * "Term Mark" is not a concept in the code. It is a MarkType row somebody created and named.
 */
export const listMarkTypes = (): Promise<MarkTypeModel[]> => assessmentTypesClient.listMarkTypes();

export const createMarkType = (model: IMarkTypeWriteModel): Promise<MarkTypeModel> => assessmentTypesClient.createMarkType(new MarkTypeWriteModel(model));

export const updateMarkType = (id: string, model: IMarkTypeWriteModel): Promise<MarkTypeModel> =>
    assessmentTypesClient.updateMarkType(id, new MarkTypeWriteModel(model));

export const deleteMarkType = (id: string): Promise<boolean> => assessmentTypesClient.deleteMarkType(id);

// ---------------------------------------------------------- assessment types

export const listAssessmentTypes = (includeArchived = false): Promise<AssessmentTypeModel[]> =>
    assessmentTypesClient.listAssessmentTypes(includeArchived);

export const createAssessmentType = (model: IAssessmentTypeWriteModel): Promise<AssessmentTypeModel> =>
    assessmentTypesClient.createAssessmentType(new AssessmentTypeWriteModel(model));

export const updateAssessmentType = (id: string, model: IAssessmentTypeWriteModel): Promise<AssessmentTypeModel> =>
    assessmentTypesClient.updateAssessmentType(id, new AssessmentTypeWriteModel(model));

export const archiveAssessmentType = (id: string): Promise<AssessmentTypeModel> => assessmentTypesClient.archiveAssessmentType(id);

export const deleteAssessmentType = (id: string): Promise<boolean> => assessmentTypesClient.deleteAssessmentType(id);

// -------------------------------------------------- assessment *group* types

/**
 * The vocabulary a procedure's selectors match on ("Term", "Division", "Section"). Selectors target
 * the *type*, never a specific group node, which is what lets one procedure apply across modules.
 * Renaming or deleting one of these therefore has reach well beyond a label.
 */
export const listGroupTypes = (includeArchived = false): Promise<AssessmentTypeModel[]> =>
    assessmentTypesClient.listGroupTypes(includeArchived);

export const createGroupType = (model: IAssessmentTypeWriteModel): Promise<AssessmentTypeModel> =>
    assessmentTypesClient.createGroupType(new AssessmentTypeWriteModel(model));

export const updateGroupType = (id: string, model: IAssessmentTypeWriteModel): Promise<AssessmentTypeModel> =>
    assessmentTypesClient.updateGroupType(id, new AssessmentTypeWriteModel(model));

export const archiveGroupType = (id: string): Promise<AssessmentTypeModel> => assessmentTypesClient.archiveGroupType(id);

export const deleteGroupType = (id: string): Promise<boolean> => assessmentTypesClient.deleteGroupType(id);

// ------------------------------------------------------------- comment types

export const listCommentTypes = (): Promise<CommentTypeModel[]> => commentsClient.listTypes();

export const createCommentType = (model: ICommentTypeWriteModel): Promise<CommentTypeModel> => commentsClient.createType(new CommentTypeWriteModel(model));

export const updateCommentType = (id: string, model: ICommentTypeWriteModel): Promise<CommentTypeModel> =>
    commentsClient.updateType(id, new CommentTypeWriteModel(model));

export const deleteCommentType = (id: string): Promise<boolean> => commentsClient.deleteType(id);
